export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    const timeout = window.setTimeout(() => {
      image.onload = null
      image.onerror = null
      reject(new Error('Image loading timed out. Please try another image.'))
    }, 30000)
    image.onload = () => { window.clearTimeout(timeout); resolve(image) }
    image.onerror = () => { window.clearTimeout(timeout); reject(new Error('Could not read this image. Please choose another file.')) }
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  fileName: string = 'cropped.webp'
): Promise<File> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('No 2d context')
  }

  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  // Preserve alpha for logos and other transparent image assets.
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  ctx.drawImage(
    image,
    -pixelCrop.x,
    -pixelCrop.y,
    image.naturalWidth || image.width,
    image.naturalHeight || image.height
  )

  // Output WebP so the result is one of the allowed image formats (PNG / WebP
  // / AVIF / SVG) — the previous JPEG output would be rejected by the upload
  // allowlist. Force the filename extension to match the encoded bytes.
  const name = fileName.replace(/\.[^.]+$/, '') + '.webp'
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'))
        return
      }
      resolve(new File([blob], name, { type: 'image/webp' }))
    }, 'image/webp', 0.92)
  })
}
